import type { CmmdDeviceRegistryDevice, CmmdDeviceRegistryRuntime } from "@cmmd-forge/contracts";
import { RefreshCwIcon } from "lucide-react";

import {
  type CloudEnvironmentCatalogRow,
  type CloudProjectCatalogRow,
  type CmmdForgeRuntimeListSnapshot,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import type { CmmdForgeRuntimeLifecycleAction } from "../../environments/primary/cmmdForgeRuntimeApi";
import { groupCloudEnvironmentRows } from "../../cmmd/cloudEnvironmentGrouping";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { CloudEnvironmentRows } from "./CloudEnvironmentRows";
import { ITEM_ROW_CLASSNAME } from "./executionTargetRow.shared";
import {
  dedupeExecutableRegistryRuntimes,
  getUnavailableLinkedDevices,
  LinkedRuntimeExecutionRow,
  UnavailableLinkedDeviceRow,
} from "./LinkedRuntimeExecutionRow";
import { SettingsSection } from "./settingsLayout";

export interface CloudExecutionTargetsSectionProps {
  readonly cloudSnapshot: CmmdForgeRuntimeListSnapshot;
  readonly registryDevices: ReadonlyArray<CmmdDeviceRegistryDevice>;
  readonly registryRuntimes: ReadonlyArray<CmmdDeviceRegistryRuntime>;
  readonly registryError: string | null;
  readonly registryIsLoading: boolean;
  readonly isRefreshing: boolean;
  readonly busyCloudProjectKey: string | null;
  readonly busyCloudActionLabel: string | null;
  readonly onRefresh: () => void;
  readonly onAddCloudEnvironment: () => void;
  readonly onRepairCloudWorkspace: (row: CloudProjectCatalogRow) => void;
  readonly onRecoverCloudEnvironment?: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  /**
   * Destroys the Sprite AND removes the Forge Cloud registration behind the
   * row — the row's one destructive action, behind a type-to-confirm gate.
   */
  readonly onDeleteEnvironment: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  readonly onRemoveCloudProject: (row: CloudProjectCatalogRow) => void;
  /** A rename landed: refetch so the row, the label and the delete gate move. */
  readonly onEnvironmentRenamed: () => void;
  readonly onUpdateCloudRuntime: (environment: CloudEnvironmentCatalogRow) => void;
  readonly onRunCloudRuntimeLifecycleAction: (
    row: CloudProjectCatalogRow | CloudEnvironmentCatalogRow,
    action: CmmdForgeRuntimeLifecycleAction,
  ) => void;
}

/**
 * The Connections page's two lists: cloud Environments, then remote ones. It
 * owns no state, so the caller decides what each row action does.
 */
export function CloudExecutionTargetsSection({
  cloudSnapshot,
  registryDevices,
  registryRuntimes,
  registryError,
  registryIsLoading,
  isRefreshing,
  onRefresh,
  onAddCloudEnvironment,
  ...rowProps
}: CloudExecutionTargetsSectionProps) {
  const cloudEnvironmentGroups = groupCloudEnvironmentRows(
    cloudSnapshot.environments,
    cloudSnapshot.rows,
  );
  const hasCloudRows = cloudEnvironmentGroups.length > 0;
  // Lifted out of the device half so the row list is assembled in one place.
  const deviceRuntimes = dedupeExecutableRegistryRuntimes(registryRuntimes);
  const unavailableDevices = getUnavailableLinkedDevices({
    devices: registryDevices,
    executableRuntimes: deviceRuntimes,
  });
  return (
    <>
      {/* Two sections, not one list with mini labels inside it. The reference
          gives each run of rows a full heading and its own header action, and
          `SettingsSection` already is that heading — reaching for it means the
          page's rhythm comes from the settings standard rather than from
          chrome invented here. */}
      <SettingsSection
        title="Cloud environments"
        headerAction={<RefreshButton isRefreshing={isRefreshing} onRefresh={onRefresh} />}
      >
        <ExecutionTargetError message={cloudSnapshot.error} source="Cloud" />
        <ExecutionTargetLoadingRow show={cloudSnapshot.isLoading && !hasCloudRows}>
          Loading cloud environments...
        </ExecutionTargetLoadingRow>
        <CloudEnvironmentRows groups={cloudEnvironmentGroups} {...rowProps} />
        <ExecutionTargetEmptyState
          onRefresh={onRefresh}
          onAddCloudEnvironment={onAddCloudEnvironment}
          showAdd={!hasCloudRows && cloudSnapshotSettled(cloudSnapshot)}
          what="cloud environment"
        />
      </SettingsSection>
      <DeviceExecutionTargets
        runtimes={deviceRuntimes}
        unavailableDevices={unavailableDevices}
        error={registryError}
        isLoading={registryIsLoading}
        onRefresh={onRefresh}
      />
    </>
  );
}

/**
 * A snapshot that has really answered "none", as opposed to one that has not
 * loaded, is stale, is mid-fetch, or failed — the cases where offering Add
 * would be offering it against an absence the page cannot vouch for.
 */
function cloudSnapshotSettled(snapshot: CmmdForgeRuntimeListSnapshot): boolean {
  return (
    snapshot.connected &&
    snapshot.fetchedAt !== null &&
    !snapshot.stale &&
    !snapshot.isLoading &&
    snapshot.error === null
  );
}

function RefreshButton({
  isRefreshing,
  onRefresh,
}: {
  readonly isRefreshing: boolean;
  readonly onRefresh: () => void;
}) {
  return (
    <Button size="xs" variant="outline" disabled={isRefreshing} onClick={onRefresh}>
      <RefreshCwIcon className={isRefreshing ? "size-3 animate-spin" : "size-3"} />
      {isRefreshing ? "Refreshing" : "Refresh"}
    </Button>
  );
}

function DeviceExecutionTargets(props: {
  readonly runtimes: ReadonlyArray<CmmdDeviceRegistryRuntime>;
  readonly unavailableDevices: ReadonlyArray<CmmdDeviceRegistryDevice>;
  readonly error: string | null;
  readonly isLoading: boolean;
  readonly onRefresh: () => void;
}) {
  const hasRows = props.runtimes.length > 0 || props.unavailableDevices.length > 0;
  return (
    // "Remote environments" is the reference's name for exactly this thing.
    // "Devices" described the hardware; what the user is choosing between is
    // places their work can run.
    <SettingsSection title="Remote environments">
      <ExecutionTargetError message={props.error} source="Device" />
      <ExecutionTargetLoadingRow show={props.isLoading && props.runtimes.length === 0}>
        Loading remote environments...
      </ExecutionTargetLoadingRow>
      {props.runtimes.map((runtime) => (
        <LinkedRuntimeExecutionRow key={`${runtime.source}-${runtime.id}`} runtime={runtime} />
      ))}
      {props.unavailableDevices.map((device) => (
        <UnavailableLinkedDeviceRow key={device.id} device={device} />
      ))}
      <ExecutionTargetEmptyState
        onRefresh={props.onRefresh}
        showAdd={!hasRows && !props.isLoading && props.error === null}
        what="remote environment"
      />
    </SettingsSection>
  );
}

/**
 * A failure, attributed to the half of the list it came from.
 *
 * The two sources used to be told apart by which heading a notice sat under.
 * With one list there is no heading to sit under, so the notice says it —
 * which is better anyway: the old arrangement had a device-registry failure
 * rendering above the Devices header and visually blaming Sprites.
 */
function ExecutionTargetError({
  message,
  source,
}: {
  readonly message: string | null;
  readonly source: "Cloud" | "Device";
}) {
  if (message === null) return null;
  return (
    <div className={ITEM_ROW_CLASSNAME}>
      <p className="text-xs text-destructive-foreground">
        <span className="font-medium">{source} connections:</span> {message}
      </p>
    </div>
  );
}

function ExecutionTargetLoadingRow({
  show,
  children,
}: {
  readonly show: boolean;
  readonly children: string;
}) {
  if (!show) return null;
  return (
    <div className={ITEM_ROW_CLASSNAME}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner className="size-3.5" />
        {children}
      </div>
    </div>
  );
}

/**
 * An empty state that tells you what to do next, and gives you the way there.
 *
 * It used to be one muted sentence naming two things the user could not reach
 * from this page — a description of an absence rather than a route out of it.
 */
function ExecutionTargetEmptyState({
  showAdd,
  what,
  onRefresh,
  onAddCloudEnvironment,
}: {
  readonly showAdd: boolean;
  readonly what: string;
  readonly onRefresh: () => void;
  readonly onAddCloudEnvironment?: () => void;
}) {
  if (!showAdd) return null;
  return (
    <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-border px-3 py-6 sm:px-4">
      <p className="text-sm text-muted-foreground">
        No {what} yet. An empty list on a page that loads over the network is more often &ldquo;not
        loaded&rdquo; than &ldquo;none&rdquo;.
      </p>
      <Button size="xs" variant="outline" onClick={onRefresh}>
        <RefreshCwIcon className="size-3" />
        Check again
      </Button>
      {onAddCloudEnvironment === undefined ? null : (
        <Button size="xs" onClick={onAddCloudEnvironment}>
          Add
        </Button>
      )}
    </div>
  );
}
